import { useEffect, useRef, useState } from 'react';
import * as api from '@api/gridgio/api-function';
import {
  useAnnotationPhotosQuery,
  useCheckAnnotationPhotoUploadMutation,
  useRemoveAnnotationPhotoMutation,
} from '@hook/gridgio/useApi';
import { takeNativePhoto, uploadNativePhoto, removeNativePhoto } from '@util/native-photo';
import type { INativePhoto } from '@util/native-photo';

interface IPendingPhoto {
  annotationKey: string;
  photo: INativePhoto;
  photoKey?: string;
  saved?: boolean;
}

const releasePhoto = async (pending: IPendingPhoto) => {
  await Promise.allSettled([
    removeNativePhoto(pending.photo.fileId),
    ...(pending.photoKey && !pending.saved ? [api.removeAnnotationPhoto(pending.photoKey)] : []),
  ]);
};

// 촬영을 시작한 손상 key를 유지해 선택 변경 후 다른 손상에 첨부하지 않는다.
export const useAnnotationPhoto = (workKey: string, annotationKey: string) => {
  const photoQuery = useAnnotationPhotosQuery(annotationKey);
  const { mutateAsync: checkUpload } = useCheckAnnotationPhotoUploadMutation();
  const { mutateAsync: removePhoto } = useRemoveAnnotationPhotoMutation();
  const pendingRef = useRef<IPendingPhoto | null>(null);
  const busyRef = useRef(false);
  const sessionRef = useRef(0);
  const [busy, setBusy] = useState(false);
  const [retryable, setRetryable] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setRetryable(false);
    setMessage('');
    return () => {
      ++sessionRef.current;
      const pending = pendingRef.current;
      if (pending && !busyRef.current) void releasePhoto(pending);
      pendingRef.current = null;
    };
  }, [workKey]);

  const upload = async (pending: IPendingPhoto, session: number) => {
    if (session !== sessionRef.current) return;
    if (pending.photoKey) {
      // 이전 PUT 또는 확인 요청의 응답만 유실됐다면 재업로드하지 않고 저장 완료를 확인한다.
      const confirmed = await checkUpload({ annotationKey: pending.annotationKey, photoKey: pending.photoKey });
      pending.saved = confirmed !== null;
    } else {
      const { filename, contentType, filesize } = pending.photo;
      const created = await api.createAnnotationPhoto({
        annotationKey: pending.annotationKey,
        filename,
        contentType,
        filesize,
      });
      pending.photoKey = created.data.key;
    }
    if (session !== sessionRef.current) return;
    const photoKey = pending.photoKey;
    if (!photoKey) throw new Error('사진 업로드 정보를 확인하지 못했습니다.');
    if (!pending.saved) {
      const target = await api.createAnnotationPhotoUpload(photoKey);
      if (session !== sessionRef.current) return;
      const result = await uploadNativePhoto(pending.photo, target.data.uploadUrl);
      if (result.status !== 'success') throw new Error(result.error || '사진을 업로드하지 못했습니다.');
      if (session !== sessionRef.current) return;
      const confirmed = await checkUpload({ annotationKey: pending.annotationKey, photoKey });
      if (!confirmed) throw new Error('사진 업로드를 확인하지 못했습니다. 다시 시도해주세요.');
      pending.saved = true;
    }
    await removeNativePhoto(pending.photo.fileId);
    if (session === sessionRef.current) {
      pendingRef.current = null;
      setRetryable(false);
      setMessage('');
    }
  };

  const capture = async () => {
    if (!annotationKey || busyRef.current || pendingRef.current) return;
    const selectedKey = annotationKey;
    const session = sessionRef.current;
    busyRef.current = true;
    setBusy(true);
    setMessage('사진 촬영 중입니다.');
    let pending: IPendingPhoto | null = null;
    try {
      const result = await takeNativePhoto();
      if (result.status === 'cancelled') {
        if (session === sessionRef.current) setMessage('');
        return;
      }
      if (result.status !== 'success' || !result.photo) {
        throw new Error(
          result.status === 'blocked'
            ? '앱 설정에서 카메라 접근을 허용해주세요.'
            : result.status === 'denied'
              ? '카메라 접근을 허용해주세요.'
              : result.error || '카메라를 열지 못했습니다.',
        );
      }
      pending = { annotationKey: selectedKey, photo: result.photo };
      if (session !== sessionRef.current) return;
      pendingRef.current = pending;
      setMessage('사진 업로드 중입니다.');
      await upload(pending, session);
    } catch (error) {
      if (session === sessionRef.current) {
        setRetryable(Boolean(pendingRef.current));
        setMessage(error instanceof Error ? error.message : '사진을 저장하지 못했습니다. 다시 시도해주세요.');
      }
    } finally {
      if (session !== sessionRef.current && pending) await releasePhoto(pending);
      busyRef.current = false;
      setBusy(false);
    }
  };

  const retry = async () => {
    const pending = pendingRef.current;
    if (!pending || busyRef.current) return;
    const session = sessionRef.current;
    busyRef.current = true;
    setBusy(true);
    setMessage('사진 업로드 중입니다.');
    try {
      await upload(pending, session);
    } catch {
      if (session === sessionRef.current) setMessage('사진을 저장하지 못했습니다. 다시 시도해주세요.');
    } finally {
      if (session !== sessionRef.current) await releasePhoto(pending);
      busyRef.current = false;
      setBusy(false);
    }
  };

  const cancel = async () => {
    const pending = pendingRef.current;
    if (!pending || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await releasePhoto(pending);
      pendingRef.current = null;
      setRetryable(false);
      setMessage('');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const remove = async (photoKey: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await removePhoto({ annotationKey, photoKey });
      setMessage('');
    } catch {
      setMessage('사진을 삭제하지 못했습니다. 다시 시도해주세요.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return { photos: photoQuery.data?.data ?? [], photoQuery, busy, retryable, message, capture, retry, cancel, remove };
};
